const { Decimal } = require('decimal.js');
Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_EVEN });

/**
 * @param {any | null | undefined} entity
 * @returns {boolean}
 */
const isNullish = (entity) => entity === null || entity === undefined;

/**
 *
 * @param {boolean | null | undefined} enabled
 * @param {FeeType | null | undefined} type
 * @param {string | null | undefined} value
 * @returns {FeeInput}
 */
function Fee(enabled, type, value) {
  this.enabled = !!enabled;

  /** @type {FeeType} */
  this.type = type === '$' ? '$' : '%';

  this.value = value && !new Decimal(value).isNaN() ? `${value}` : '0.00';
  return this;
}

/**
 * @description Maps LegacyVirtualTerminalSettings to FeeConfigInput
 *
 * @param {DatabaseFeeSettings} settings
 * @param {Invoice | null} invoice
 * @returns {{feeConfig: FeeConfigInput, options: FeeOptions}}
 */
const mapDatabaseFeeSettings = (settings, invoice = null) => {
  // Map for Custom Fee (cardUpcharge)
  if (!settings) throw new Error('Database fee settings are required.');

  let invoiceSettings, virtualTerminalSettings;
  if (settings && typeof settings === 'object') {
    invoiceSettings = settings.invoiceSettings || {};
    virtualTerminalSettings = settings.virtualTerminalSettings || {};
  } else {
    throw new Error('Invalid fee settings input.');
  }

  // Set up fee options object
  /** @type {FeeOptions} */
  const options = {
    dualPricing: !!settings?.DualFeeEnabled,
    taxLast: !!settings?.applyTaxLast,
  };

  if (
    !isNullish(virtualTerminalSettings.surchargeByState) &&
    !isNullish(virtualTerminalSettings.surchargeByStateRates)
  ) {
    /** @type {SurchargeByStateOptions} */
    const surchargeByStateOptions = {
      enabled: !!virtualTerminalSettings.surchargeByState,
      rates: virtualTerminalSettings.surchargeByStateRates,
    };
    options.surchargeByState = surchargeByStateOptions;
  }

  // Map for Late Fee
  /** @type {FeeInput} */
  const lateFee = new Fee(
    !!invoiceSettings.CustomFeeOneEnabled,
    invoiceSettings.CustomFeeOneType,
    invoiceSettings.CustomFeeOne ? `${invoiceSettings.CustomFeeOne}` : '0.00',
  );

  // If late fee (CustomFeeOne) is enabled, calculate the due date and add to options
  if (
    !isNullish(invoice) &&
    invoice?.dateOpened &&
    invoiceSettings.CustomFeeOneEnabled &&
    invoiceSettings.CustomFeeOneDueDays
  ) {
    let lateFeeEffectiveDate = new Date(invoice.dateOpened);
    if (typeof invoiceSettings.CustomFeeOneDueDays === 'number' && invoiceSettings.CustomFeeOneDueDays >= 0) {
      lateFeeEffectiveDate.setDate(lateFeeEffectiveDate.getDate() + invoiceSettings.CustomFeeOneDueDays);
      options['lateFeeEffectiveDate'] = lateFeeEffectiveDate;
    }
  }

  /** @type {FeeInput} */
  const cardUpcharge = new Fee(
    virtualTerminalSettings?.InvoiceDiscount,
    virtualTerminalSettings?.InvoiceSVCFeeType,
    virtualTerminalSettings.InvoiceSVCFee ? `${virtualTerminalSettings.InvoiceSVCFee}` : '0.00',
  );

  // Map for Surcharge Fee
  /** @type {FeeInput} */
  const surcharge = new Fee(
    virtualTerminalSettings.InvoiceSurcharge,
    virtualTerminalSettings.InvoiceSurchargeFeeType,
    virtualTerminalSettings.InvoiceSurchargeFee ? `${virtualTerminalSettings.InvoiceSurchargeFee}` : '0.00',
  );

  // Map for Tax
  /** @type {FeeInput} */
  const tax = new Fee(
    virtualTerminalSettings?.InvoiceTax,
    virtualTerminalSettings?.InvoiceSalesTaxFeeType,
    virtualTerminalSettings?.InvoiceSalesTaxFee ? `${virtualTerminalSettings?.InvoiceSalesTaxFee}` : '0.00',
  );

  const feeConfig = {
    cardUpcharge,
    surcharge,
    lateFee,
    tax,
  };

  return { feeConfig, options };
};

/**
 * @description enables the feeConfig from a calculation result to be used for another calculation.
 *
 * @param {Record<FeeOutputName, FeeObject>} fees
 * @returns {import('../../types/fees').FeeConfigInput}
 */
function mapCalculatedFeesToFeeConfigInput(fees) {
  if (!fees || typeof fees !== 'object') {
    throw new Error(`'fees' object is invalid.`);
  }

  return {
    cardUpcharge: {
      enabled: !!fees.cardUpcharge?.enabled,
      type: fees.cardUpcharge?.type === '$' ? '$' : '%',
      value: fees.cardUpcharge?.value?.stored || '0.00',
    },
    surcharge: {
      enabled: !!fees.surcharge?.enabled,
      type: fees.surcharge?.type === '$' ? '$' : '%',
      value: fees.surcharge?.value?.stored || '0.00',
    },
    lateFee: {
      enabled: !!fees.lateFee?.enabled,
      type: fees.lateFee?.type === '$' ? '$' : '%',
      value: fees.lateFee?.value?.stored || '0.00',
    },
    tax: {
      enabled: !!fees.tax?.enabled,
      type: fees.tax?.type === '$' ? '$' : '%',
      value: fees.tax?.value?.stored || '0.00',
    },
  };
}

module.exports = {
  mapDatabaseFeeSettings,
  mapCalculatedFeesToFeeConfigInput,
};

/**
 * @typedef {import('../../types/fees').FeeInput} FeeInput
 * @typedef {import('../../types/fees').Fee} FeeObject
 * @typedef {import('../../types/fees').FeeOutputName} FeeOutputName
 * @typedef {import('../../types/fees').FeeType} FeeType
 * @typedef {import('../../types/fees').FeeOptions} FeeOptions
 * @typedef {import('../../types/fees').SurchargeByStateOptions} SurchargeByStateOptions
 * @typedef {import('../../types/fees').FeeConfigInput} FeeConfigInput
 * @typedef {import('../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings
 * @typedef {import('../../types/invoices').IInvoiceModel} Invoice
 */
